<script>
import { mapGetters } from 'vuex';
import { get } from 'lodash';

import Base from './base.vue';

import plansMixin from '@/plans/mx/plan';
import { contactUsPath } from '@/common/constants';
import { PBX_REGIONS, regions } from '@/plans/ZOOM_PHONE/helper/constants';
import { dateTimeFormat } from '@/utils/dateformat.js';
import { getDownSellLicenseDesc } from '@/utils/validate-downsell';
import { checkAddonLicenseSubject2Base, getMinimumSubjectLicense } from '../utils';

export default {
  name: 'Edit',
  extends: Base,
  mixins: [plansMixin],
  data() {
    return {
      isExpanded: true,
    };
  },
  computed: {
    ...mapGetters(['boughtPlans', 'getSelectedPlans', 'assignInfo', 'basicConfig', 'getUserCurrentSubscriptions', 'plancodeMapSubscriptionDisabled']),

    cycleVisible() {
      return false
    },
    planDetailLabel() {
      if (this.isBulkLicenseAccount || this.fromCampaignLink) return '';
      return this.$t('billing.zone.explore_other_plans_edit');
    },
    addRegionVisible() {
      // can only edit one regional plan at the same time for bk account
      if (this.isBulkLicenseAccount) {
        const boughtWOFreeTrial = this.boughtSelfPlans.filter(p => !p.isFreeTrial);
        if (boughtWOFreeTrial.length || boughtWOFreeTrial.length === this.pbxRegions.length) {
          return false;
        }
      }
      if (this.fromCampaignLink) return false;
      return this.callingSelected?.length !== this.callingOptions?.length;
    },
    pbxRegions() {
      return regions.filter((x) => {
        const hasBought = this.boughtSelfPlans.find(p => p.region === x.key);
        if (hasBought?.isFreeTrial) return false;

        if (x.key === PBX_REGIONS.JP){
           return !this.$config('hideZoomPhoneJPPlans') && !!hasBought && !this.$config('hideZoomPhoneJPPlansPhase2');
        }
        if (this.$config('hideZoomPhoneUKIRPlans')) {
          if (hasBought) return true;
          return x.key !== PBX_REGIONS.UK_AND_IRELAND;
        }
        return true;
      })
      .filter(x => this.eduPlusPlans.find(item => item.region === x.key));
    },
    callingOptions() {
      return this.pbxRegions.map((x) => {
        let min = 1;

        const hasBought = this.boughtSelfPlans.find(p => p.region === x.key);
        if (hasBought && this.disableDownSell) {
          min = hasBought.cartInfo.selected_number;
        }
        // disable decrease button
        if (hasBought && hasBought.isStandardPrice === false && this.hasNonStandard) {
          min = hasBought.cartInfo.selected_number;
        }
        if (hasBought && (this.fromCampaignLink || this.isBulkLicenseAccount)) {
          // increase only, disable the decrease button until the total is greater than the minium amount
          if (this.totalLicenseAmount <= getMinimumSubjectLicense()) {
            const o = this.selfPlan.find(p => p.region == x.key)
            if (o) {
              min = o.cartInfo.selected_number
            }
          }
        }

        return {
          id: x.key,
          name: this.$t(x.label),
          desc: '',
          min: Math.max(min || 0, 1),
          max: 9999,
        }
      });
    },
    hasNonStandard() {
      return this.selfPlan.find(p => p.isStandardPrice === false) && !this.hasAccountDiscount
    },

    licenseTip() {
      //
      const ret = checkAddonLicenseSubject2Base()
      if (ret?.message) return ret.message

      //
      let needAssignTotalQuantity = 0;
      let expireTime = null;
      this.selfPlan.forEach(p => {
        const oldPlan = this.boughtPlans.find(c => c.plan_code === p.plan_code);
        const nextPlan = this.getSelectedPlans.find(c => c.plan_code === p.plan_code);
        const { assignLicenseQuantity = 0, planExpireTime } = get(this.assignInfo, p.plan_code, {})

        const ret = getDownSellLicenseDesc({
          oldPlan,
          nextPlan,
          quantity: p.cartInfo.selected_number,
          assignLicenseQuantity,
          planExpireTime
        });
        if (ret?.needAssignQuantity > 0) {
          needAssignTotalQuantity += ret?.needAssignQuantity;
        }
        if (!expireTime) {
          expireTime = ret?.planExpireTime;
        }
      });
      if (needAssignTotalQuantity > 0) {
        return expireTime
          ? this.$t('billing.error.downgrade_alert_info', [needAssignTotalQuantity, dateTimeFormat(expireTime)])
          : this.$t('billing.error.downgrade_alert_info_no_date', [needAssignTotalQuantity])
      }

      //
      const hasSameQuantity = this.selfPlan.some((c) => {
        const o = this.boughtPlans.find((b) => b.plan_code === c.plan_code)
        if (o) {
          return o.cartInfo?.selected_number === c.cartInfo?.selected_number
        }
        return false
      })
      if (hasSameQuantity) {
        if (this.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }
        if (this.hasNonStandard) {
          return this.$t('billing.planform.meeting_license_custom_pricing')
        }
        return ''
      }

      return ''
    }
  },

  methods: {
    viewDetails() {
      this.openFullList()
    },

    getCallingPlanDisable(plan) {
      const hasBought = this.boughtSelfPlans.find(p => p.plan_code === plan.plan_code);

      if (hasBought && this.isBulkLicenseAccount) {
        return {
          disabled: false,
          tooltip: this.$t('billing.error.buld_acount_license'),
          shouldDisabled: true
        }
      }

      if (hasBought && this.disableDownSell) {
        return {
          disabled: false,
          tooltip: '',
          shouldDisabled: true
        }
      }

      if (hasBought && this.fromCampaignLink) {
        return {
          shouldDisabled: true,
          disabled: false,
        };
      }

      if (this.plancodeMapSubscriptionDisabled[plan.plan_code]) {
        return {
          disabled: true,
          tooltip: this.$t('billing.zobizplus.pending_change')
        };
      }

      if ((this.basicConfig?.isDirectSales && hasBought) || hasBought?.ratePlans?.length > 1) {
        return {
          disabled: false,
          tooltips: '',
          disableSwitch: true,
          disableDelete: true,
          deleteTips: this.$t('billing.cancel_disable_contact_us', {
            url: contactUsPath
          }),
        }
      }

      return {
        disabled: false,
        tooltip: ''
      }
    },
  }
}
</script>
